import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { restaurantConfig } from "@/config/restaurantConfig";

const ThemeContext = createContext(null);
const STORAGE_KEY = "fuditap_theme";

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    const scopes = document.querySelectorAll(".emergent-pwa-scope");
    scopes.forEach((el) => {
      el.classList.toggle("dark", theme === "dark");
    });
    localStorage.setItem(STORAGE_KEY, theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#0c0a09" : "#F97316");

    // Apply tenant brand colors to design tokens (API-ready personalization).
    const b = restaurantConfig.brand?.[theme];
    if (b) {
      [root, ...scopes].forEach((el) => {
        el.style.setProperty("--primary", b.primary);
        el.style.setProperty("--ring", b.primary);
        el.style.setProperty("--brand-from", b.from);
        el.style.setProperty("--brand-to", b.to);
      });
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
